// React Query wiring for the pitch deck (step 8).
//
// Generation runs on the streaming Lambda Function URL (like the other ai-*
// generators): the AI authors the whole deck and output scales with project
// size, past API Gateway's 30s. The deck is persisted server-side at
// DECK#LATEST; useDeck reads it, generation refreshes it. There's no curation —
// regenerate overwrites, and the PM edits by copying the markdown out.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import { useConfig } from '@/app/ConfigProvider';
import { useAuth } from '@/components/auth/AuthProvider';
import { consumeNdjsonStream } from '@/services/stream';
import { useStreamPreview } from '@/hooks/use-stream-preview';
import type { DeckProposal, DeckSpec, StreamedSlide } from '@/types/api';

export function useDeck(projectId: string) {
  return useQuery<DeckProposal | null>({
    queryKey: ['deck', projectId],
    queryFn: async () => {
      const res = await api.getDeck(projectId);
      if (!res.success) throw new Error(res.message || 'Failed to load deck');
      return res.data ?? null;
    },
    enabled: !!projectId,
  });
}

export function useGenerateDeck() {
  const queryClient = useQueryClient();
  const config = useConfig();
  const { getStreamAuthHeaders } = useAuth();
  const preview = useStreamPreview<StreamedSlide>('slides');

  const mutation = useMutation<DeckSpec, Error, string>({
    mutationFn: async (projectId: string) => {
      preview.reset();
      const data = await consumeNdjsonStream<{ deck?: DeckSpec }>(
        `${config.api.streamUrl}/ai/deck`,
        { projectId },
        getStreamAuthHeaders(),
        preview.onChunk,
      );
      if (!data.deck) throw new Error('Deck generation did not complete');
      return data.deck;
    },
    // Returning the invalidations keeps the mutation pending until the fresh
    // deck is in cache — the progress panel hands off straight to the new deck.
    onSuccess: (_data, projectId) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['deck', projectId] }),
        queryClient.invalidateQueries({ queryKey: ['project', projectId] }),
      ]),
  });

  return {
    ...mutation,
    streamedSlides: preview.items,
    streamStarted: preview.started,
    draftingTitle: preview.draftingTitle,
  };
}
